// Call plugin.
import * as eventTypes from '../eventTypes'
import * as actionTypes from '../actionTypes'
import { OPERATIONS, OP_TRANSITIONS } from '../constants'
import { getCallById } from '../selectors'

// Handlers.
import {
  callOperationHandler,
  callEventHandler,
  stateChangeHandler,
  trackAddedHandler,
  trackRemovedHandler
} from './handlers'

const callEvents = {}
/*
  "Media" actions are:
    - ADD_MEDIA
    - ADD_MEDIA_FINISH
    - ADD_BASIC_MEDIA

    - REMOVE_MEDIA
    - REMOVE_MEDIA_FINISH
    - REMOVE_BASIC_MEDIA

    - CALL_HOLD
    - CALL_HOLD_FINISH
    - CALL_UNHOLD
    - CALL_UNHOLD_FINISH

    - CALL_REMOTE_HOLD_FINISH
    - CALL_REMOTE_UNHOLD_FINISH
    - CALL_REMOTE_ADD_MEDIA_FINISH
    - CALL_REMOTE_REMOVE_MEDIA_FINISH

    - REPLACE_TRACK
    - REPLACE_TRACK_FINISH

    - REMOTE_START_MOH_FINISH
    - REMOTE_STOP_MOH_FINISH
 */

callEvents[actionTypes.ADD_MEDIA] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.ADD_MEDIA,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.ADD_MEDIA_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.ADD_MEDIA,
      transition: OP_TRANSITIONS.FINISH,
      isLocal: action.payload.local
    }),
    // Notify that local track is available after new media is added to the Call.
    trackAddedHandler(action, params),
    callEventHandler(eventTypes.CALL_ADDED_MEDIA, action, {
      local: action.payload.local,
      tracks: action.payload.tracks,
      mediaId: action.payload.mediaId,
      error: action.payload.error
    })
  ]
}

callEvents[actionTypes.ADD_BASIC_MEDIA] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.ADD_BASIC_MEDIA,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

// -----

callEvents[actionTypes.REMOVE_MEDIA] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.REMOVE_MEDIA,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.REMOVE_MEDIA_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.REMOVE_MEDIA,
      transition: OP_TRANSITIONS.FINISH,
      isLocal: action.payload.local
    }),
    // Notify that local track is not available after it has been removed from Call.
    trackRemovedHandler(action),
    callEventHandler(eventTypes.CALL_REMOVED_MEDIA, action, {
      local: action.payload.local,
      tracks: action.payload.tracks,
      error: action.payload.error
    })
  ]
}

callEvents[actionTypes.REMOVE_BASIC_MEDIA] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.REMOVE_BASIC_MEDIA,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

// -----

callEvents[actionTypes.CALL_HOLD] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.HOLD,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.CALL_HOLD_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.HOLD,
      isLocal: true,
      transition: OP_TRANSITIONS.FINISH
    }),
    // Notify that local&remote tracks are not available after Call is held.
    trackRemovedHandler(action),
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.CALL_UNHOLD] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.UNHOLD,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.CALL_UNHOLD_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.UNHOLD,
      isLocal: true,
      transition: OP_TRANSITIONS.FINISH
    }),
    // Notify that local&remote tracks are available after Call is unheld.
    trackAddedHandler(action, params),
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.CALL_REMOTE_HOLD_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.HOLD,
      isLocal: false,
      transition: OP_TRANSITIONS.FINISH
    }),
    // Notify that local&remote tracks are not available after Call is remotely held.
    trackRemovedHandler(action),
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.CALL_REMOTE_UNHOLD_FINISH] = (action, params) => {
  /*
   * Special-case: If the call was previously on remote hold with a track, then that
   *    track was MoH and is being removed as part of the unhold operation. Need to
   *    also emit an event to notify that the track is gone.
   * This is needed because "stop MoH" and "unhold" are done in a single renegotiation
   *    instead of as two.
   */
  let trackRemovedEvent
  const prevCall = getCallById(params.prevState, action.payload.id)
  if (prevCall.localHold === false && prevCall.remoteHold === true && prevCall.remoteTracks.length > 0) {
    // Get the tracks from prevCall that are not in newCall.
    const newCall = getCallById(params.state, action.payload.id)
    const removedTracks = prevCall.remoteTracks.filter(id => !newCall.remoteTracks.includes(id))
    // Generate a "removed" event for those tracks, if any.
    if (removedTracks.length > 0) {
      // Create a fake action to pass to the `trackRemovedHandler` helper to create the event.
      const fakeAction = { payload: { id: action.payload.id, remoteTracks: removedTracks } }
      trackRemovedEvent = trackRemovedHandler(fakeAction)
    }
  }

  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.UNHOLD,
      isLocal: false,
      transition: OP_TRANSITIONS.FINISH
    }),
    // If there was MoH before, notify that it is not available now.
    trackRemovedEvent,
    // Notify that local&remote tracks are available after Call is remotely unheld.
    trackAddedHandler(action, params),
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.REMOTE_ADD_MEDIA_FINISH] = (action, params) => {
  // Notify that remote track is available after it has been added remotely.
  return trackAddedHandler(action, params)
}

callEvents[actionTypes.REMOTE_REMOVE_MEDIA_FINISH] = (action, params) => {
  // Notify that remote track is not available after it has been removed remotely.
  return trackRemovedHandler(action)
}

// -----

callEvents[actionTypes.REPLACE_TRACK] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.REPLACE_TRACK,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.REPLACE_TRACK_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.REPLACE_TRACK,
      transition: OP_TRANSITIONS.FINISH,
      isLocal: true
    }),
    callEventHandler(eventTypes.CALL_TRACK_REPLACED, action, {
      error: action.payload.error,
      newTrackId: action.payload.newTrackId,
      oldTrack: action.payload.oldTrackState
    })
  ]
}

// -----

callEvents[actionTypes.REMOTE_START_MOH_FINISH] = (action, params) => {
  return trackAddedHandler(action, params)
}

callEvents[actionTypes.REMOTE_STOP_MOH_FINISH] = action => {
  return trackRemovedHandler(action)
}

export default callEvents
